import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, History } from 'lucide-react';
import type { DemandaEsteira } from '../../../types';
import { FILAS } from './filas';
import { agruparPorDia, diaLocal } from './normalizar';

interface Props {
    demandas: DemandaEsteira[];
    /** Clicar num dia abre o nome do cliente — o detalhe, como no resto do painel. */
    onAbrirDetalhe: (d: DemandaEsteira) => void;
}

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho',
    'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

/**
 * Modo Calendário — o trabalho no tempo.
 *
 * Cada demanda cai no dia do PRAZO quando tem um (é o que tem consequência), e
 * no dia em que parou quando não tem. Responde "o que venceu esta semana?" e
 * "em que dia o atraso começou a se acumular?".
 *
 * ⚠️ Quase tudo fica no passado, e isso é correto: o painel só guarda o que está
 * travado — documento dentro do prazo não é demanda. Por isso o que é anterior
 * ao mês vira um balde visível no topo em vez de sumir na navegação.
 */
const CalendarioDemandas: React.FC<Props> = ({ demandas, onAbrirDetalhe }) => {
    const hoje = new Date();
    const [cursor, setCursor] = useState({ ano: hoje.getFullYear(), mes: hoje.getMonth() });
    const [diaAberto, setDiaAberto] = useState<string | null>(diaLocal(hoje.toISOString()));

    const { dias, antes, depois } = useMemo(
        () => agruparPorDia(demandas, cursor.ano, cursor.mes),
        [demandas, cursor]
    );

    // Grade de 6 semanas: começa no domingo anterior ao dia 1.
    const celulas = useMemo(() => {
        const primeiro = new Date(cursor.ano, cursor.mes, 1, 12);
        const inicio = new Date(primeiro);
        inicio.setDate(1 - primeiro.getDay());
        return Array.from({ length: 42 }, (_, i) => {
            const d = new Date(inicio);
            d.setDate(inicio.getDate() + i);
            const chave = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            return { chave, dia: d.getDate(), doMes: d.getMonth() === cursor.mes };
        });
    }, [cursor]);

    const maior = Math.max(1, ...[...dias.values()].map(l => l.length));
    const chaveHoje = diaLocal(hoje.toISOString());
    const doDia = diaAberto ? dias.get(diaAberto) ?? [] : [];

    const mover = (delta: number) => setCursor(c => {
        const d = new Date(c.ano, c.mes + delta, 1);
        return { ano: d.getFullYear(), mes: d.getMonth() };
    });

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                    <button onClick={() => mover(-1)} aria-label="Mês anterior"
                        className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <ChevronLeft size={18} />
                    </button>
                    <div className="text-center">
                        <h3 className="font-bold text-gray-900 dark:text-white">{MESES[cursor.mes]} {cursor.ano}</h3>
                        <button
                            onClick={() => setCursor({ ano: hoje.getFullYear(), mes: hoje.getMonth() })}
                            className="text-[11px] text-primary hover:underline"
                        >
                            voltar para hoje
                        </button>
                    </div>
                    <button onClick={() => mover(1)} aria-label="Próximo mês"
                        className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <ChevronRight size={18} />
                    </button>
                </div>

                {/* Balde do passado. Sem ele, 1.000 demandas antigas ficariam
                    escondidas meses para trás e o calendário pareceria em dia. */}
                {antes.length ? (
                    <div className="mb-3 flex items-center gap-2 text-xs rounded-lg bg-red-50 dark:bg-red-900/15 text-red-700 dark:text-red-400 px-3 py-2">
                        <History size={14} className="shrink-0" />
                        <span>
                            <strong>{antes.length}</strong> demanda{antes.length > 1 ? 's' : ''} com data anterior a {MESES[cursor.mes].toLowerCase()} — ainda em aberto.
                        </span>
                    </div>
                ) : null}

                <div className="grid grid-cols-7 gap-1">
                    {SEMANA.map(s => (
                        <div key={s} className="text-[10px] font-bold text-gray-400 dark:text-gray-500 text-center pb-1">{s}</div>
                    ))}
                    {celulas.map(c => {
                        const qtd = dias.get(c.chave)?.length ?? 0;
                        const aberto = diaAberto === c.chave;
                        // Intensidade proporcional ao dia mais cheio do mês — o
                        // olho acha o pico sem ler número nenhum.
                        const intensidade = qtd ? Math.max(0.12, qtd / maior) : 0;
                        return (
                            <button
                                key={c.chave}
                                onClick={() => setDiaAberto(c.chave)}
                                disabled={!c.doMes}
                                aria-label={`${c.dia} de ${MESES[cursor.mes]}: ${qtd} demanda${qtd === 1 ? '' : 's'}`}
                                aria-pressed={aberto}
                                className={`relative h-14 sm:h-16 rounded-lg text-left p-1.5 transition-all border ${
                                    !c.doMes ? 'opacity-30 border-transparent cursor-default'
                                        : aberto ? 'border-primary ring-2 ring-primary/30'
                                            : 'border-gray-100 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500'
                                }`}
                            >
                                {qtd ? (
                                    <span
                                        className="absolute inset-0 rounded-lg bg-primary"
                                        style={{ opacity: intensidade * 0.35 }}
                                        aria-hidden="true"
                                    />
                                ) : null}
                                <span className={`relative text-[11px] font-semibold ${
                                    c.chave === chaveHoje ? 'text-white bg-primary rounded-full px-1.5' : 'text-gray-600 dark:text-gray-300'
                                }`}>
                                    {c.dia}
                                </span>
                                {qtd ? (
                                    <span className="relative block text-sm sm:text-base font-black text-gray-900 dark:text-white mt-0.5">
                                        {qtd}
                                    </span>
                                ) : null}
                            </button>
                        );
                    })}
                </div>

                {depois.length ? (
                    <p className="mt-3 text-[11px] text-gray-400 dark:text-gray-500">
                        + {depois.length} com data depois deste mês.
                    </p>
                ) : null}
            </div>

            {/* Painel do dia: a lista do que caiu naquela data. */}
            <aside className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 self-start">
                <h4 className="text-sm font-bold text-gray-900 dark:text-white">
                    {diaAberto
                        ? new Date(`${diaAberto}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })
                        : 'Escolha um dia'}
                </h4>
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mb-3">
                    {doDia.length} demanda{doDia.length === 1 ? '' : 's'} com prazo ou parada neste dia
                </p>

                {doDia.length === 0 ? (
                    <p className="text-xs text-gray-400 dark:text-gray-500 py-6 text-center">Nada neste dia.</p>
                ) : (
                    <ul className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                        {doDia.map(d => (
                            <li key={d.id}>
                                <button
                                    onClick={() => onAbrirDetalhe(d)}
                                    className="w-full text-left rounded-lg px-2.5 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 group"
                                >
                                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold mb-1 ${FILAS[d.fila].badge}`}>
                                        {FILAS[d.fila].titulo}
                                    </span>
                                    <p className="text-xs font-semibold text-gray-800 dark:text-gray-100 truncate group-hover:text-primary">
                                        {d.cliente_nome || 'Cliente sem nome'}
                                    </p>
                                    <p className="text-[10px] text-gray-400 dark:text-gray-500">
                                        {d.prazo_em ? 'prazo' : 'parado'} · {d.dias_parado <= 0 ? 'hoje' : `${d.dias_parado}d`}
                                    </p>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </aside>
        </div>
    );
};

export default CalendarioDemandas;
